import { FC, useMemo } from 'react'
import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'src/components/molecules/Table'
import { format } from 'date-fns'
import { Image as ImageIcon, Loader, RefreshCw } from 'lucide-react'
import { IconTrash } from '@tabler/icons-react'
import { Project } from 'src/types/projects'
import { LayerUploadStatus } from 'src/types/layer'
import DeleteProjectDialog from 'src/components/organisms/dialogs/DeleteProjectDialog'
import EditProjectDialog from 'src/components/organisms/dialogs/EditProjectDialog'
import EditProjectAccess from 'src/components/organisms/dialogs/EditProjectAccessDialog'
import IdCell from 'src/components/atoms/IdCell'
import TooltipWrapper from 'src/components/atoms/TooltipWrapper'
import { JumpingDots } from 'src/components/atoms/JumpingDots'

interface ProjectTableProps {
  projects: Project[]
  isLoading: boolean
  onRefreshThumbnail?: (projectId: string) => void
  // Infinite-scroll sentinel lives inside the scroll container so it triggers on internal scroll.
  loadMoreRef?: (node?: Element | null) => void
  isFetchingNextPage?: boolean
  hasNextPage?: boolean
}

export const ProjectTable: FC<ProjectTableProps> = ({
  projects,
  isLoading,
  onRefreshThumbnail,
  loadMoreRef,
  isFetchingNextPage,
  hasNextPage,
}) => {
  const columns = useMemo<ColumnDef<Project>[]>(
    () => [
      {
        id: 'id',
        accessorKey: 'id',
        header: 'ID',
        cell: (info) => <IdCell id={info.getValue()} />,
      },
      {
        accessorKey: 'thumbnailUrl',
        header: 'Thumbnail',
        cell: ({ row }) => (
          <div className="relative h-16 w-24">
            {row.original.thumbnailUrl ? (
              <>
                <img
                  src={row.original.thumbnailUrl}
                  alt={`${row.original.name} thumbnail`}
                  className="h-full w-full rounded object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none'
                    e.currentTarget.nextElementSibling?.classList.remove('hidden')
                  }}
                />
                <button
                  onClick={() => onRefreshThumbnail?.(row.original.id)}
                  className="absolute right-1 top-1 rounded-full bg-white/80 p-1 hover:bg-white focus:outline-none focus:ring-2 focus:ring-primary"
                  title="Refresh thumbnail"
                >
                  <RefreshCw className="h-4 w-4 text-gray-600" />
                </button>
              </>
            ) : null}
            <div
              className={`absolute inset-0 flex items-center justify-center rounded bg-secondary ${
                row.original.thumbnailUrl ? 'hidden' : ''
              }`}
            >
              <ImageIcon className="h-6 w-6 text-muted-foreground" />
            </div>
            <button
              onClick={() => onRefreshThumbnail?.(row.original.id)}
              className="absolute right-1 top-1 rounded-full bg-white/80 p-1 hover:bg-white focus:outline-none focus:ring-2 focus:ring-primary"
              title="Refresh thumbnail"
            >
              <RefreshCw className="h-4 w-4 text-gray-600" />
            </button>
          </div>
        ),
      },
      {
        id: 'isPublic',
        accessorKey: 'isPublic',
        header: 'Public',
        cell: ({ getValue }) => (getValue() ? 'Yes' : 'No'),
      },
      {
        id: 'isArchived',
        accessorKey: 'isArchived',
        header: 'Archived',
        cell: ({ getValue }) => (getValue() ? 'Yes' : 'No'),
      },
      {
        id: 'name',
        header: 'Project Name',
        cell: ({
          row: {
            original: { name },
          },
        }) => <span className="font-bold">{name}</span>,
      },
      {
        accessorKey: 'location',
        header: 'Country',
      },
      {
        accessorKey: 'totalAreaHectares',
        header: 'Total Area (ha)',
        cell: ({ getValue }) => getValue<number>().toLocaleString(),
      },
      {
        accessorKey: 'createdAt',
        header: 'Created Date',
        cell: ({ getValue }) => format(new Date(getValue() as string), 'MMM dd, yyyy'),
      },
      {
        accessorKey: 'updatedAt',
        header: 'Last Updated',
        cell: ({ getValue }) => format(new Date(getValue() as string), 'MMM dd, yyyy'),
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => {
          const isBeingCreated = [
            LayerUploadStatus.UPLOADING,
            LayerUploadStatus.PROCESSING,
            LayerUploadStatus.PENDING,
          ].includes(row.original.layerUploadStatus)
          return (
            <div className="flex">
              <EditProjectDialog project={row.original} />
              <EditProjectAccess project={row.original} />
              {isBeingCreated ? (
                <TooltipWrapper content="Cannot delete — project is still being created" side="top">
                  <span className="ml-2 flex h-10 w-16 cursor-not-allowed items-center justify-center rounded-full border border-gray-300 text-gray-300">
                    <IconTrash size={24} />
                  </span>
                </TooltipWrapper>
              ) : (
                <DeleteProjectDialog project={row.original} requireConfirmation />
              )}
            </div>
          )
        },
      },
    ],
    [onRefreshThumbnail],
  )

  const table = useReactTable({
    data: projects,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  if (isLoading) {
    return (
      <div className="flex h-[50vh] items-center justify-center pt-10 text-light-green-500">
        <Loader />
      </div>
    )
  }

  return (
    <div className="relative max-h-[calc(100vh-400px)] min-h-[200px] overflow-y-auto rounded-md">
      <Table>
        <TableHeader className="sticky top-0 bg-gray-800">
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id}>{flexRender(header.column.columnDef.header, header.getContext())}</TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
              ))}
            </TableRow>
          ))}
          {projects.length === 0 && (
            <TableRow>
              <TableCell colSpan={columns.length} className="text-center">
                No projects found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {hasNextPage && (
        <div ref={loadMoreRef} className="py-2 text-center">
          {isFetchingNextPage && <JumpingDots className="text-light-green-50" />}
        </div>
      )}
    </div>
  )
}
